// models/workFlow/UIActionModel.ts

export interface FunctionParameter {
  paramName: string;
  paramType: string;
  isRequired: boolean;
  description?: string;
}

export interface AzureFunctionModel {
  functionId: number;
  tenantId: number;
  connectionId: number;
  functionName: string;
  functionUrl: string;
  requestGroup?: string;
  functionCode?: string;
  description?: string;
  inputParameters: FunctionParameter[];
  outputParameters: FunctionParameter[];
}

/** A single row in the parameter mapping table */
export interface UIActionParameterMapping {
  id: number;
  /** Name of the Azure Function input parameter */
  functionParam: string;
  /** Type of the function parameter */
  paramType: string;
  /** Whether this param is required by the function */
  isRequired: boolean;
  /** Source: system variable, manual value, or previous output */
  sourceType: "system" | "manual" | "output";
  /** Key of the system parameter (when sourceType === "system") */
  systemParamKey?: string;
  /** Hardcoded value (when sourceType === "manual") */
  manualValue?: string;
}

/** The full flow configuration saved as workflowJson in ExecutionFlowModel */
export interface UIActionFlowConfig {
  connectionId: number;
  triggerType: string;
  selectedFunctionId?: number;
  functionName?: string;
  functionUrl?: string;
  parameterMappings: UIActionParameterMapping[];
}

/** A system-provided mail / context parameter that users can map to function inputs */
export interface SystemParameter {
  key: string;
  label: string;
  type: string;
  description: string;
  category: "mail" | "user" | "tenant";
}

/** Default mail / user / tenant fields for parameter mapping in the admin UI */
export const SYSTEM_PARAMETERS: SystemParameter[] = [
  // Mail parameters
  {
    key: "mailSubject",
    label: "Mail Subject",
    type: "string",
    description: "Subject line of the email",
    category: "mail",
  },
  {
    key: "mailBody",
    label: "Mail Body (HTML)",
    type: "string",
    description: "HTML body of the email",
    category: "mail",
  },
  {
    key: "mailBodyText",
    label: "Mail Body (Plain Text)",
    type: "string",
    description: "Plain text version of the email body",
    category: "mail",
  },
  {
    key: "mailFrom",
    label: "Mail From (Sender)",
    type: "string",
    description: "Email address of the sender",
    category: "mail",
  },
  {
    key: "mailFromDisplayName",
    label: "Sender Display Name",
    type: "string",
    description: "Display name of the sender",
    category: "mail",
  },
  {
    key: "mailTo",
    label: "Mail To (Recipients)",
    type: "string",
    description: "Comma-separated recipient email addresses",
    category: "mail",
  },
  {
    key: "mailCC",
    label: "Mail CC",
    type: "string",
    description: "Comma-separated CC email addresses",
    category: "mail",
  },
  {
    key: "mailBCC",
    label: "Mail BCC",
    type: "string",
    description: "Comma-separated BCC email addresses",
    category: "mail",
  },
  {
    key: "mailReceivedTime",
    label: "Received Time",
    type: "datetime",
    description: "ISO 8601 timestamp when email was received",
    category: "mail",
  },
  {
    key: "mailSentTime",
    label: "Sent Time",
    type: "datetime",
    description: "ISO 8601 timestamp when email was sent",
    category: "mail",
  },
  {
    key: "mailId",
    label: "Mail ID",
    type: "string",
    description: "Unique identifier of the email",
    category: "mail",
  },
  {
    key: "mailConversationId",
    label: "Conversation ID",
    type: "string",
    description: "Thread/conversation identifier",
    category: "mail",
  },
  {
    key: "mailAttachments",
    label: "Mail Attachments",
    type: "array",
    description: "List of email attachment objects",
    category: "mail",
  },
  {
    key: "mailAttachmentCount",
    label: "Attachment Count",
    type: "number",
    description: "Number of attachments",
    category: "mail",
  },
  {
    key: "mailImportance",
    label: "Importance",
    type: "string",
    description: "Email importance: low, normal, high",
    category: "mail",
  },
  {
    key: "mailCategories",
    label: "Categories",
    type: "array",
    description: "List of email categories/labels",
    category: "mail",
  },
  // User parameters
  {
    key: "userEmail",
    label: "User Email",
    type: "string",
    description: "Email address of the logged-in user",
    category: "user",
  },
  {
    key: "userDisplayName",
    label: "User Display Name",
    type: "string",
    description: "Display name of the logged-in user",
    category: "user",
  },
  {
    key: "userId",
    label: "User ID",
    type: "number",
    description: "Internal user ID",
    category: "user",
  },
  // Tenant parameters
  {
    key: "tenantId",
    label: "Tenant ID",
    type: "number",
    description: "Internal numeric tenant ID",
    category: "tenant",
  },
  {
    key: "tenantAzureId",
    label: "Azure Tenant ID",
    type: "string",
    description: "Azure AD tenant GUID",
    category: "tenant",
  },
];

export const TRIGGER_TYPE_OPTIONS = [
  {
    value: "New Mail",
    label: "New Mail",
    description: "Triggered when a new email",
  },
  {
    value: "Reply To Mail",
    label: "Reply To Mail",
    description: "Triggered when replying to an email",
  },
  {
    value: "Forward Mail",
    label: "Forward Mail",
    description: "Triggered when forwarding an email",
  },
  {
    value: "New and Reply",
    label: "New and Reply",
    description: "Triggered for new emails and replies",
  },
  {
    value: "New and Forward",
    label: "New and Forward",
    description: "Triggered for new emails and forwards",
  },
];
